import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_CONTENT_LOCALE,
  type TranslationReplacementAction,
} from '@course-authoring/shared-types';
import {
  useCourseMetadataMutation,
  useCourseMetadataQuery,
} from '../../api/courseMetadataApi';
import type { TranslationReplacementView } from '../../components/TranslationStatusPrimitives';
import type { CourseDraft } from './types';
import {
  useCourseMetadataAutosave,
  type CourseMetadataDraft,
} from './courseMetadata/useCourseMetadataAutosave';

interface UseCourseMetadataEditorOptions {
  courseId: string | undefined;
  selectedLocale: string;
  metadataAccountId: number | null;
  isTranslationMode: boolean;
  updateCourseDraft: (updates: Partial<CourseDraft>) => void;
}

export function useCourseMetadataEditor({
  courseId,
  selectedLocale,
  metadataAccountId,
  isTranslationMode,
  updateCourseDraft,
}: UseCourseMetadataEditorOptions) {
  const [sessionReplacementView, setSessionReplacementView] = useState<'prompt' | 'preserved' | null>(null);

  const {
    data: metadataProjection,
    isLoading: isLoadingMetadata,
    error: metadataError,
  } = useCourseMetadataQuery(courseId ?? '', {
    enabled: Boolean(courseId),
    locale: selectedLocale,
    accountId: metadataAccountId,
  });
  const {
    data: englishMetadataProjection,
    isLoading: isLoadingEnglishMetadata,
    error: englishMetadataError,
  } = useCourseMetadataQuery(courseId ?? '', {
    enabled: Boolean(courseId) && isTranslationMode,
    locale: DEFAULT_CONTENT_LOCALE,
    accountId: metadataAccountId,
  });
  const metadataMutation = useCourseMetadataMutation(courseId ?? '', {
    locale: selectedLocale,
    accountId: metadataAccountId,
    onError: (error) => {
      console.error('Failed to save course metadata:', error);
    },
  });

  const saveMetadata = useCallback((patch: Parameters<typeof metadataMutation.mutateAsync>[0]['patch']) => (
    metadataMutation.mutateAsync({
      patch,
      locale: selectedLocale,
      accountId: metadataAccountId,
    })
  ), [metadataAccountId, metadataMutation.mutateAsync, selectedLocale]);

  const handleMetadataSaved = useCallback((
    projection: NonNullable<typeof metadataProjection>,
    patch: Parameters<typeof metadataMutation.mutateAsync>[0]['patch'],
  ) => {
    updateCourseDraft({
      title: projection.title,
      description: projection.description,
      certificateMessage: projection.certificateMessage,
      completedMessage: projection.completedMessage,
    });
    if (patch.replacementAction === 'preserve') {
      setSessionReplacementView('preserved');
    } else if (patch.replacementAction === 'queue') {
      setSessionReplacementView(null);
    }
  }, [updateCourseDraft]);

  const metadataAutosave = useCourseMetadataAutosave({
    enabled: Boolean(metadataProjection) && metadataProjection?.editable !== false,
    scopeKey: `${courseId ?? ''}:${selectedLocale}:${metadataAccountId ?? 'base'}`,
    initialProjection: metadataProjection,
    save: saveMetadata,
    onSaved: handleMetadataSaved,
  });

  useEffect(() => {
    setSessionReplacementView(null);
  }, [courseId, metadataAccountId, selectedLocale]);

  const persistedQueued = (metadataProjection?.summary.pendingHumanTranslationCount ?? 0) > 0;
  const replacementView: TranslationReplacementView | null = sessionReplacementView === 'prompt'
    ? 'prompt'
    : sessionReplacementView === 'preserved'
      ? 'preserved'
      : persistedQueued
        ? 'queued'
        : null;

  const handleTranslatableFieldChange = useCallback(() => {
    if (
      selectedLocale === DEFAULT_CONTENT_LOCALE
      && (metadataProjection?.summary.humanTranslationCount ?? 0) > 0
      && replacementView === null
    ) {
      setSessionReplacementView('prompt');
    }
  }, [metadataProjection?.summary.humanTranslationCount, replacementView, selectedLocale]);

  const handleReplacementDecision = useCallback(async (action: TranslationReplacementAction) => {
    try {
      // The autosave hook includes dirty source fields in this PATCH when
      // needed and sends a decision-only PATCH after the source is already
      // saved, keeping both timing paths immediate and atomic.
      await metadataAutosave.saveDecision(action);
    } catch {
      // The autosave hook owns the error state and mutation logging.
    }
  }, [metadataAutosave.saveDecision]);

  const handleChangeReplacementDecision = useCallback(() => {
    setSessionReplacementView('prompt');
  }, []);

  const updateMetadata = useCallback((updates: Partial<CourseMetadataDraft>) => {
    handleTranslatableFieldChange();
    metadataAutosave.setDraft((current) => ({ ...current, ...updates }));
    updateCourseDraft(updates);
  }, [handleTranslatableFieldChange, metadataAutosave.setDraft, updateCourseDraft]);

  return {
    metadataProjection,
    englishMetadataProjection,
    isLoadingMetadata,
    isLoadingEnglishMetadata,
    metadataError,
    englishMetadataError,
    isMetadataSaving: metadataMutation.isPending,
    metadataAutosave,
    replacementView,
    updateMetadata,
    handleReplacementDecision,
    handleChangeReplacementDecision,
  };
}
